import { useMemo } from 'react';

import type { Ati } from '@atlassian/ari';

import { useUniversalCreateState } from '../context';

/**
 * Hook that determines customized ATIs to pass to the useUCSuggestions hook based on the specified type.
 *
 * This hook handles the logic for processing customized ATIs based on:
 * - Suggestion configuration state
 * - ATI type (preferred or exclude)
 *
 * @param atiType - The type of ATIs to retrieve ('preferred' | 'exclude')
 * @returns An array of ATI strings to pass to useUCSuggestions, or undefined if not applicable
 */
export function useSuggestionCustomizeAtis(atiType: 'preferred' | 'exclude'): string[] | undefined {
	// Access the Universal Create state
	const { suggestionConfig } = useUniversalCreateState();

	// Determine ATIs to pass to the hook
	const atis = useMemo(() => {
		if (!suggestionConfig.enabled) {
			return undefined;
		}

		const atiArray =
			atiType === 'preferred' ? suggestionConfig.preferredAtis : suggestionConfig.excludeAtis;

		if (!atiArray) {
			return undefined;
		}

		// Convert Ati[] to string[]
		if (Array.isArray(atiArray) && atiArray.length > 0) {
			return atiArray.map((ati: Ati) => ati.toString());
		}

		// Default fallback
		return undefined;
	}, [suggestionConfig, atiType]);

	return atis;
}

/**
 * Hook that determines the preferred ATIs to pass to the useUCSuggestions hook.
 */
export function useSuggestionPreferredAtis(): string[] | undefined {
	return useSuggestionCustomizeAtis('preferred');
}

/**
 * Hook that determines the exclude ATIs to pass to the useUCSuggestions hook.
 */
export function useSuggestionExcludeAtis(): string[] | undefined {
	return useSuggestionCustomizeAtis('exclude');
}
